import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import { COMPANY } from '../mock';

const LAST_UPDATED = 'February 2026';

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-[22px] md:text-[26px] font-semibold text-slate-900 tracking-tight">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-[15.5px] leading-relaxed text-slate-600">
      {children}
    </div>
  </section>
);

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white text-slate-900 antialiased overflow-x-hidden">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13.5px] text-slate-500 hover:text-slate-900 transition-colors"
            data-testid="terms-back-home"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[12px] font-medium text-slate-700 uppercase tracking-[0.18em]">
            Terms of Service
          </div>

          <h1 className="mt-4 font-display text-[40px] md:text-[52px] leading-[1.05] font-semibold tracking-tight text-slate-900">
            Straightforward{' '}
            <span className="bg-gradient-to-r from-violet-500 to-indigo-500 bg-clip-text text-transparent">
              terms.
            </span>
          </h1>
          <p className="mt-5 text-[16px] leading-relaxed text-slate-600">
            These Terms of Service ("Terms") govern your access to and use of the {COMPANY.fullName} website and the initial enquiry channels made available through it. Formal engagements are governed by a separate written agreement. By using this website you agree to these Terms. Last updated: {LAST_UPDATED}.
          </p>

          <Section title="1. About us">
            <p>
              {COMPANY.fullName} is a boutique engineering consultancy based in Melbourne, Australia. In these Terms, "we", "us" and "our" refer to {COMPANY.fullName}, and "you" or "your" refers to the individual or entity accessing this website.
            </p>
          </Section>

          <Section title="2. Nature of the website">
            <p>
              This website is provided for general information and initial contact purposes only. Content on the site including case studies, statistics, service descriptions and articles is for illustrative purposes and does not constitute professional, legal, financial or technical advice.
            </p>
            <p>
              Nothing on this website constitutes an offer to provide services on specified terms. Any engagement will be scoped, priced and governed by a separate written agreement signed by both parties.
            </p>
          </Section>

          <Section title="3. Enquiries and initial conversations">
            <p>
              When you submit our contact form or otherwise reach out to us, we will use the information you provide to respond to your enquiry. Initial exploratory conversations are provided in good faith and do not create a client relationship or any obligation to deliver services.
            </p>
            <p>
              Please do not share confidential, sensitive or regulated information through the contact form. If information sharing is required, we will put a mutual non-disclosure agreement in place first.
            </p>
          </Section>

          <Section title="4. Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the website in any way that breaches applicable law or regulation.</li>
              <li>Attempt to gain unauthorised access to the website, its infrastructure or any related systems.</li>
              <li>Introduce malware, run automated attacks, or interfere with the availability, integrity or performance of the website.</li>
              <li>Scrape, harvest or reuse content from the website for commercial purposes without our written permission.</li>
              <li>Impersonate any person or entity when contacting us.</li>
            </ul>
          </Section>

          <Section title="5. Intellectual property">
            <p>
              All content on this website including text, graphics, logos, imagery, code and page layouts is owned by or licensed to {COMPANY.fullName} and is protected by copyright, trademark and other intellectual property laws.
            </p>
            <p>
              You may view, download and print pages from the website for your own personal or internal business reference, provided you retain all copyright and proprietary notices. Any other use, including reproduction, republication, redistribution or public display, requires our prior written consent.
            </p>
          </Section>

          <Section title="6. Third-party links and content">
            <p>
              The website may contain links to third-party websites or resources. We do not control and are not responsible for the content, accuracy, availability or practices of any third-party site. Accessing linked sites is at your own risk and subject to the terms of those sites.
            </p>
          </Section>

          <Section title="7. Disclaimers">
            <p>
              The website is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied. To the extent permitted by law, we disclaim all implied warranties including merchantability, fitness for a particular purpose and non-infringement.
            </p>
            <p>
              We do not warrant that the website will be uninterrupted, error-free, secure, or free of viruses or other harmful components. You are responsible for taking your own precautions.
            </p>
          </Section>

          <Section title="8. Limitation of liability">
            <p>
              To the maximum extent permitted by law, {COMPANY.fullName} and its officers, employees and contractors will not be liable for any indirect, incidental, special, consequential or punitive damages, or any loss of profits, revenue, data, goodwill or opportunity, arising out of or in connection with your use of the website, even if we have been advised of the possibility of such damages.
            </p>
            <p>
              Where liability cannot be excluded, our aggregate liability arising out of or in connection with the website is limited, at our option, to resupplying the information or paying the cost of having the information resupplied.
            </p>
            <p>
              Nothing in these Terms limits or excludes any consumer guarantees or rights that cannot lawfully be limited or excluded under applicable law, including the Australian Consumer Law.
            </p>
          </Section>

          <Section title="9. Indemnity">
            <p>
              You agree to indemnify and hold {COMPANY.fullName} harmless from any claims, damages, losses and expenses (including reasonable legal fees) arising out of your breach of these Terms, your misuse of the website, or your violation of any applicable law or the rights of any third party.
            </p>
          </Section>

          <Section title="10. Privacy">
            <p>
              Our handling of personal information is described in our{' '}
              <Link to="/privacy" className="text-violet-600 hover:underline">
                Privacy Policy
              </Link>
              , which forms part of these Terms.
            </p>
          </Section>

          <Section title="11. Changes to these Terms">
            <p>
              We may update these Terms from time to time. When we do, we will revise the "Last updated" date at the top of this page. Your continued use of the website after changes are posted constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="12. Governing law and jurisdiction">
            <p>
              These Terms are governed by the laws of the State of Victoria, Australia. You agree to submit to the exclusive jurisdiction of the courts of Victoria and any courts competent to hear appeals from those courts.
            </p>
          </Section>

          <Section title="13. Contact us">
            <p>
              Questions about these Terms can be sent to:
            </p>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 text-[14.5px]">
              <div className="font-semibold text-slate-900">{COMPANY.fullName}</div>
              <div className="mt-1 text-slate-600">Level 27, 101 Collins St, Melbourne VIC 3000, Australia</div>
              <div className="mt-1">
                <a href={`mailto:${COMPANY.email}`} className="text-violet-600 hover:underline">
                  {COMPANY.email}
                </a>
              </div>
            </div>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
